import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { ChartBar, Copy, ArrowClockwise, Trash, LinkSimple, ArrowSquareOut } from "@phosphor-icons/react";

export function ClientReportShare({ businessId, shareId, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const link = shareId ? `${window.location.origin}/client-report/${shareId}` : "";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/client-report/share`);
      onChange(data.client_report_share_id);
      toast.success(shareId ? "New link generated (old one revoked)" : "Client report link created");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/businesses/${businessId}/client-report/share`);
      onChange(null);
      toast.success("Client report link revoked");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — select the link and copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="client-report-btn" className="px-5 py-2.5 rounded-md text-sm font-semibold border inline-flex items-center gap-2 transition-colors hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-accent)", color: "var(--bk-accent)" }}>
          <ChartBar size={18} weight="duotone" /> Client report
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Client-facing financial report</DialogTitle>
        </DialogHeader>
        <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
          A polished, printable monthly report — P&amp;L summary, top categories, savings wins, and Rowan&apos;s
          highlights note. Shows totals and top 5 categories only, so it&apos;s safe to send to clients,
          lenders, or partners. No login required; regenerate or revoke the link anytime.
        </p>

        {shareId ? (
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-md border" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }}>
              <LinkSimple size={18} color="var(--bk-text-2)" />
              <input data-testid="client-report-link-input" readOnly value={link} className="flex-1 bg-transparent text-sm outline-none font-mono" style={{ color: "var(--bk-text)" }} />
              <button onClick={copy} data-testid="copy-client-report-link" className="p-1.5 rounded hover:bg-white" title="Copy">
                <Copy size={18} weight="duotone" color="var(--bk-accent)" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={link} target="_blank" rel="noreferrer" data-testid="open-client-report-link" className="bk-btn-primary px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                <ArrowSquareOut size={16} weight="bold" /> Open report
              </a>
              <button onClick={generate} disabled={busy} data-testid="regenerate-client-report" className="px-4 py-2 rounded-md text-sm font-semibold border inline-flex items-center gap-2 hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                <ArrowClockwise size={16} weight="bold" /> Regenerate
              </button>
              <button onClick={revoke} disabled={busy} data-testid="revoke-client-report" className="px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2" style={{ color: "#B23B1F" }}>
                <Trash size={16} weight="bold" /> Revoke
              </button>
            </div>
          </div>
        ) : (
          <button onClick={generate} disabled={busy} data-testid="create-client-report-link" className="bk-btn-primary px-6 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 mt-2 disabled:opacity-60">
            <LinkSimple size={18} weight="bold" /> {busy ? "Creating…" : "Create client report link"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
